/**
 * Normalize list/API datetime values for HTML date/time inputs and PKT parsers.
 * List endpoints often return ISO strings without a separate *_time field.
 */

export type DateTimeParts = {
  date: string // YYYY-MM-DD in Asia/Karachi
  time: string // HH:MM in Asia/Karachi
}

/** Split an ISO or date-only string into PKT date + time parts. */
export function splitIsoToPktDateTime(raw: string | null | undefined): DateTimeParts | null {
  if (raw == null) return null
  const value = String(raw).trim()
  if (!value) return null

  if (value.includes("T")) {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return null
    return {
      date: d.toLocaleDateString("en-CA", { timeZone: "Asia/Karachi" }),
      time: d.toLocaleTimeString("en-GB", {
        timeZone: "Asia/Karachi",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    }
  }

  // Already YYYY-MM-DD (optionally with space time)
  const datePart = value.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null
  const timeMatch = value.match(/\b(\d{2}:\d{2})(?::\d{2})?\b/)
  return {
    date: datePart,
    time: timeMatch?.[1] || "",
  }
}

/**
 * Apply date/time normalization onto formData via synthetic input events.
 * Returns true if any field was written.
 */
export function applyEditDateTimeNormalization(
  formData: Record<string, unknown>,
  dateField: string,
  timeField: string,
  setField: (name: string, value: string) => void,
): boolean {
  const rawDate = formData[dateField]
  if (rawDate == null || rawDate === "") return false

  const parts = splitIsoToPktDateTime(String(rawDate))
  if (!parts) return false

  let changed = false
  if (String(rawDate).includes("T") || String(rawDate).length > 10) {
    if (formData[dateField] !== parts.date) {
      setField(dateField, parts.date)
      changed = true
    }
  }
  const existingTime = formData[timeField]
  if ((!existingTime || existingTime === "") && parts.time) {
    setField(timeField, parts.time)
    changed = true
  }
  return changed
}
